import mapboxgl from 'mapbox-gl';
import marker from './components/marker';
import layers from './components/layer';
import legends from './components/legend';
import { delay } from '../utils/async';
import { ILayer, IMarker } from '../types/common';

const images = Object.freeze({});

const map = (config) => {
  const instance = new mapboxgl.Map({
    style: 'mapbox://styles/lanefaison/ckipolvza0d2s17p6kkrqtysr',
    zoom: 13,
    attributionControl: false,
    ...config,
  });

  instance.addControl(
    new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true,
      visualizePitch: false,
    }),
    'top-left',
  );

  if (config.geolocation) {
    instance.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      'top-left',
    );
  }

  Object.keys(images).map((key) =>
    instance.loadImage(images[key], (err, image) => {
      if (err) return;
      instance.addImage(key, image);
    }),
  );

  return instance;
};

const init = ({ token, courseId, container, ...config }) => {
  mapboxgl.accessToken = token;

  const domMap = document.querySelector(`#${container}`);
  const instance = map({ container, ...config });
  const markersMap = {};
  let courses = null;
  let userTracks = null;

  const overlapOffset = 16;

  const addMarker = (m: IMarker) => {
    const prepared = marker(m);
    const mapMarker = new mapboxgl.Marker(prepared.element)
      .setLngLat(m.coordinates)
      .addTo(instance);

    //add offset if markers are too close to each other
    for (const [id, el] of Object.entries(markersMap)) {
      const dx = m.coordinates[0] - el.coordinates[0];
      const dy = m.coordinates[1] - el.coordinates[1];
      if (dx === 0 && dy === 0) {
        el.offsets = (el.offsets || 0) + 1;
        mapMarker.setOffset([
          overlapOffset * Math.cos((Math.PI / 6) * (el.offsets - 1)),
          overlapOffset * Math.sin((Math.PI / 6) * (el.offsets - 1)),
        ]);
      }
    }

    if (
      !markersMap[m.id] ||
      m.lastUpdateDate > markersMap[m.id].lastUpdateDate
    ) {
      markersMap[m.id] = {
        ...m,
        mapMarker,
      };
    }

    const filterCourses = (coursesIds) => {
      courses =
        courses ||
        instance.queryRenderedFeatures({
          layers: ['courses'],
        });

      if (!coursesIds.length) {
        instance.getSource('highlights').setData({
          type: 'FeatureCollection',
          features: [],
        });
      } else {
        const uniqCourses = courses.reduce(
          (acct, course) => ({
            ...acct,
            [course.properties.course_id]: course,
          }),
          {},
        );

        const filtered = coursesIds
          .map((id) => uniqCourses[id])
          .filter(Boolean);
        instance.getSource('highlights').setData({
          type: 'FeatureCollection',
          features: filtered,
        });
      }
    };

    const filterUserTracks = (huntIds) => {
      userTracks =
        userTracks ||
        instance.queryRenderedFeatures({
          layers: ['userTracks'],
        });

      if (!huntIds.length) {
        instance.getSource('userTracks').setData({
          type: 'FeatureCollection',
          features: userTracks,
        });
      } else {
        const filtered = userTracks.filter((userTrack) =>
          huntIds.includes(userTrack.properties.hunt_id),
        );
        instance.getSource('userTracks').setData({
          type: 'FeatureCollection',
          features: filtered,
        });
      }
    };

    mapMarker.getElement().addEventListener('click', async (e) => {
      e.stopPropagation();

      if (!prepared.popup) return;

      const popup = new mapboxgl.Popup({
        offset: 15,
        className:
          m.type === 'trap'
            ? 'covey-marker-popup covey-marker-popup--trap'
            : 'covey-marker-popup',
      })
        .setLngLat(m.coordinates)
        .setHTML(prepared.popup)
        .addTo(instance);

      instance.fire('closePopup');
      instance.on('closePopup', popup.remove);

      popup.on('close', async () => {
        if (m.courseId) {
          filterCourses([]);
        }
        if (m.huntId) {
          filterUserTracks([]);
        }
      });

      await delay(0);
      if (m.courseId) {
        filterCourses([m.courseId]);
      }
      if (m.huntId) {
        filterUserTracks([m.huntId]);
      }
    });

    return mapMarker;
  };

  const addLegend = async (name) => {
    const legend = legends[name];
    if (!legend) throw new Error('Legend does not exist');

    domMap.append(legend());
  };

  const addLayer = async (
    name,
    params,
    beforeCallback = null,
    afterCallback = null,
  ) => {
    if (beforeCallback) {
      beforeCallback();
    }

    const layer = layers[name];
    if (!layer) throw new Error('Layer does not exist');

    const layerInstance: ILayer = await layer(params);
    instance.addSource(layerInstance.source, {
      type: 'geojson',
      data: layerInstance.data,
    });
    instance.addLayer(layerInstance.config);

    if (layerInstance.labels) {
      const labelInstance = layers.labels(layerInstance.labels);
      instance.addSource(labelInstance.source, {
        type: 'geojson',
        data: labelInstance.data,
      });
      instance.addLayer(labelInstance.config);
    }

    if (layerInstance.markers) {
      layerInstance.markers.items.forEach((item) => addMarker(item));
      Object.keys(markersMap).forEach((key) => {
        const mapItem = markersMap[key];
        const el = mapItem.mapMarker.getElement();
        el.classList.add('-last');
      });
    }

    if (layerInstance.sublayers) {
      layerInstance.sublayers.forEach((subLayer) =>
        instance.addLayer(subLayer),
      );
    }

    if (layerInstance.popup) {
      instance.on('mousemove', layerInstance.source, (e) => {
        e.target.getCanvas().style.cursor = 'pointer';
      });

      instance.on('mouseout', layerInstance.source, (e) => {
        e.target.getCanvas().style.cursor = 'grab';
      });

      instance.on('click', layerInstance.source, async (e) => {
        const original = e.features[0];
        await delay(0);

        const popup = new mapboxgl.Popup({
          offset: 15,
          className: 'covey-marker-popup covey-marker-popup--course',
        })
          .setLngLat([
            original.properties.longitude,
            original.properties.latitude,
          ])
          .setHTML(original.properties.popup)
          .addTo(instance);

        instance.getSource('highlights').setData({
          type: 'FeatureCollection',
          features: [original],
        });

        popup.on('close', () => {
          instance.getSource('highlights').setData({
            type: 'FeatureCollection',
            features: [],
          });
        });

        instance.on('closePopup', popup.remove);
      });
    }

    if (afterCallback) {
      afterCallback();
    }

    return layerInstance;
  };

  const findMarker = (id) => {
    const marker = markersMap[id];
    const center = marker ? marker.coordinates : config.center;
    const zoom = marker ? 17 : 13;
    if (!center) return;

    instance.flyTo({
      center,
      essential: true,
      zoom,
    });
  };

  const fitBounds = (coordinates) => {
    instance.fitBounds(coordinates, {
      padding: 100,
    });
  };

  const trackUserLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition((position) => {
      const userCoordinates = [
        position.coords.longitude,
        position.coords.latitude,
      ];
      addMarker({
        id: 'location',
        type: 'point',
        color: '#1da1f2',
        coordinates: userCoordinates,
      } as any);
      fitBounds(
        Object.values(markersMap).map((marker: IMarker) =>
          marker.coordinates.map(Number),
        ),
      );
    });
  };

  return new Promise((resolve) => {
    instance.on('load', () => {
      addLayer('highlights', {});
      resolve({
        instance,
        addMarker,
        findMarker,
        addLayer,
        addLegend,
        trackUserLocation,
        fitBounds,
      });
    });
  });
};

export default init;
